import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * Menu suspenso simples: abre no clique, fecha com Esc, clique fora ou ao
 * escolher. O conteudo recebe `fechar` para itens que encerram o menu.
 */
export function Menu({
  gatilho,
  rotulo,
  children,
  alinhar = 'direita',
  abrir = 'baixo',
  classeGatilho = '',
}: {
  gatilho: ReactNode;
  rotulo: string;
  children: (fechar: () => void) => ReactNode;
  alinhar?: 'direita' | 'esquerda';
  /** 'cima' para gatilho no pe da tela (rodape da barra lateral). */
  abrir?: 'baixo' | 'cima';
  classeGatilho?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: PointerEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('pointerdown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  return (
    <div ref={raiz} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={aberto}
        aria-controls={id}
        aria-label={rotulo}
        title={rotulo}
        onClick={() => setAberto((a) => !a)}
        className={classeGatilho}
      >
        {gatilho}
      </button>
      {aberto && (
        <div
          id={id}
          role="menu"
          aria-label={rotulo}
          className={`absolute z-30 min-w-56 rounded-(--radius-painel) border border-borda bg-superficie py-1 text-texto shadow-menu ${
            abrir === 'cima' ? 'bottom-full mb-1' : 'top-full mt-1'
          } ${alinhar === 'direita' ? 'right-0' : 'left-0'}`}
        >
          {children(() => setAberto(false))}
        </div>
      )}
    </div>
  );
}

export function ItemMenu({
  children,
  onClick,
  marcado,
}: {
  children: ReactNode;
  onClick: () => void;
  marcado?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={marcado}
      onClick={onClick}
      className="flex w-full items-center gap-3 px-3 text-left text-sm hover:bg-destaque-fundo"
    >
      <span aria-hidden className={`size-1.5 shrink-0 ${marcado ? 'bg-marca' : 'bg-transparent'}`} />
      {children}
    </button>
  );
}
